import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, Star } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import CertificationManager from './CertificationManager';
import { ThirdPartyRatingDashboard } from './ThirdPartyRatingDashboard';

interface CertificationDashboardProps {
  userId: string;
}

const CertificationDashboard: React.FC<CertificationDashboardProps> = ({ userId }) => {
  const { t } = useTranslation('dashboard');
  const [activeSection, setActiveSection] = useState<'certifications' | 'third-party'>('certifications');
  const certManagerRef = useRef<{ openAddForm: () => void }>(null);

  return (
    <div className="space-y-6 pb-8 md:pb-6">
      <div>
        <h2 className="text-xl font-semibold">{t('certificationManagement.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your professional certifications and third-party platform ratings
        </p>
        <Separator className="my-4" />
      </div>

      {/* Two-Column Navigation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card 
          className={`cursor-pointer transition-all duration-200 ${
            activeSection === 'certifications' 
              ? 'ring-2 ring-primary shadow-md' 
              : 'hover:shadow-md'
          }`}
          onClick={() => {
            setActiveSection('certifications');
            certManagerRef.current?.openAddForm();
          }}
        >
          <CardContent className="p-6 text-center">
            <Award className="h-8 w-8 mx-auto mb-3 text-primary" />
            <h3 className="font-semibold mb-2">Upload Certificates</h3>
            <p className="text-sm text-muted-foreground">
              Add your professional certifications, licenses, and educational credentials
            </p>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all duration-200 ${
            activeSection === 'third-party' 
              ? 'ring-2 ring-primary shadow-md' 
              : 'hover:shadow-md'
          }`}
          onClick={() => setActiveSection('third-party')}
        >
          <CardContent className="p-6 text-center">
            <Star className="h-8 w-8 mx-auto mb-3 text-primary" />
            <h3 className="font-semibold mb-2">Submit Third-Party Ratings</h3>
            <p className="text-sm text-muted-foreground">
              Share your ratings and reviews from other platforms with verification screenshots
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Content Area */}
      <div className="mt-6">
        {activeSection === 'certifications' ? (
          <CertificationManager ref={certManagerRef} userId={userId} />
        ) : (
          <ThirdPartyRatingDashboard userId={userId} />
        )}
      </div>
    </div>
  );
};

export default CertificationDashboard;